import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@shared/ui/dialog";
import { Button } from "@shared/ui/button";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import {
  useAssignConsumer,
  useConsumers,
} from "@features/mx/device-management/hooks";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { queryClient } from "@shared/api/queries/queryClient";
import { useParams } from "react-router-dom";
import { PAGE_SIZE } from "@shared/utils/constants";

interface UpdateConsumerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  meter: any;
}

export const UpdateConsumerModal: React.FC<UpdateConsumerModalProps> = ({
  open,
  onOpenChange,
  meter,
}) => {
     const { id } = useParams();
  const [selectedConsumer, setSelectedConsumer] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const { data: consumersData } = useConsumers(
    currentPage,
    10,
    1,
    searchTerm 
  );
  
  const { mutate: assignConsumer } = useAssignConsumer();

  // Handle search from GenericTable
  const handleSearch = (searchValue: string, paramName: string) => {
    setSearchTerm(searchValue);
    setCurrentPage(1); 
  };

  // Handle consumer selection
  const handleSelectConsumer = (consumer: any) => {
    setSelectedConsumer(consumer.id === selectedConsumer?.id ? null : consumer);
  };

  
  const handleAssign = () => {
    if (selectedConsumer) {
      const payload = {
        remote_utility_id: getRemoteUtilityId(),
        meter_number: meter?.meterNumber,
        consumer_no: selectedConsumer.consumerNo,
      };

      assignConsumer(payload, {
        onSuccess: () => {
          onOpenChange(false);
            queryClient.invalidateQueries({
                queryKey: ['mx', 'device', Number(id)]
                });
        },
      });
    }
  };

  // Table columns
  const columns: TableColumn<any>[] = useMemo(
    () => [
      {
        key: "consumerNo",
        header: "Account Number",
        sortable: true,
      },
      {
        key: "name",
        header: "Name",
        sortable: true,
        render: (_, row) => `${row.firstName} ${row.lastName}`,
      },
      {
        key: "categoryDisplay",
        header: "Category",
        sortable: true,
      },
      {
        key: "subCategoryDisplay",
        header: "Sub Category",
        sortable: true,
      },
      {
        key: "premise",
        header: "Premise",
        sortable: true,
        render: (_, row) => row.territoryData?.service?.premise || "—",
      },
      {
        key: "action",
        header: "Action",
        width: "w-24",
        sortable: false,
        render: (_, row) => (
          <Button
            variant={selectedConsumer?.id === row.id ? "default" : "outline"}
            size="sm"
            onClick={() => handleSelectConsumer(row)}
          >
            {selectedConsumer?.id === row.id ? "Selected" : "Select"}
          </Button>
        ),
      },
    ],
    [selectedConsumer]
  );

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setSelectedConsumer(null);
      setSearchTerm("");
      setCurrentPage(1);
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">Assign Consumer</DialogTitle>
          <DialogDescription>
            Search and select a consumer to link to this meter.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <GenericTable
            data={consumersData?.results || []}
            columns={columns}
            rowKey="id"
            searchPlaceholder="Search by name or account number..."
            searchParamName="search_data"
            searchDebounceMs={500}
            onSearch={handleSearch}
            emptyMessage="No consumers found"
          />
        </div>

        <DialogFooter className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleAssign} disabled={!selectedConsumer}>
            Assign Consumer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};